// Re-auth handling for cookie-flow auth (vouchers pattern).
//
// Two triggers redirect the page to SSO:
// 1. fetch threw a TypeError — oauth2-proxy 302s unauthenticated requests to
//    SSO; cross-origin, that 302 carries no CORS headers, so fetch rejects
//    with TypeError instead of surfacing the redirect.
// 2. fetch returned 401 — the cookie was sent but rejected; the same
//    top-level SSO round-trip refreshes it.
//
// Loop guard: the round-trip URL carries ?reauth=1 (inside the rd param). On
// the second pass shouldReauth sees the sentinel and returns false, letting
// the route render an error instead of looping forever. clearReauthSentinel
// is called once a workspace query resolves, so a recovered session gets a
// clean URL again.

import { env } from "@/lib/env";
import { ApiError } from "./error";

const REAUTH_PARAM = "reauth";

function hasSentinel(): boolean {
    return new URL(window.location.href).searchParams.get(REAUTH_PARAM) === "1";
}

export function shouldReauth(error: unknown): boolean {
    if (!env.ssoStartUrl) return false;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
    if (hasSentinel()) return false;
    if (error instanceof TypeError) return true;
    if (error instanceof ApiError && error.status === 401) return true;
    return false;
}

export function redirectToSso(): void {
    if (!env.ssoStartUrl) return;
    const url = new URL(window.location.href);
    url.searchParams.set(REAUTH_PARAM, "1");
    const rd = encodeURIComponent(url.href);
    window.location.href = `${env.ssoStartUrl}?rd=${rd}`;
}

// A query whose host can reject a session the app's own API accepts (the
// product switcher on platform's API) opts out with meta: { reauth: false };
// otherwise every round-trip back fails it again and the redirect loops.
export function reauthOnError(error: unknown, meta?: Record<string, unknown>): void {
    if (meta?.reauth === false) return;
    if (shouldReauth(error)) redirectToSso();
}

export function clearReauthSentinel(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(REAUTH_PARAM)) return;
    url.searchParams.delete(REAUTH_PARAM);
    window.history.replaceState(null, "", url.href);
}
